import {z} from 'zod/v4';

import {DOMAIN_VALUE_PREFIX} from '../types/constants';
import {RemoteValueDefinition, ValueDefinition} from '../types/value';
import {LocalizedStringSchema} from './localized';
import {TemplateValueSchema} from './template';

const commonValueDefinitionFields = {
  id: z.string().startsWith(DOMAIN_VALUE_PREFIX),
  description: LocalizedStringSchema,
};

const valueSchema = z.strictObject({
  id: z.string(),
  value: z.union([LocalizedStringSchema, TemplateValueSchema]),
  acronym: LocalizedStringSchema.optional(),
});

const customValueSchema = valueSchema.extend({
  properties: z.record(z.string(), z.unknown()),
});

const RemoteValueDefinitionSchema: z.ZodType<RemoteValueDefinition> = z.union([
  z.strictObject({$fetch: z.string(), description: LocalizedStringSchema.optional()}),
  z.strictObject({$remote: z.string().startsWith(DOMAIN_VALUE_PREFIX), description: LocalizedStringSchema.optional()}),
]);

export const ValueDefinitionSchema: z.ZodType<ValueDefinition> = z.union([
  z.strictObject({
    ...commonValueDefinitionFields,
    values: z.array(valueSchema),
  }),
  z.strictObject({
    ...commonValueDefinitionFields,
    type: z.string(),
    values: z.array(customValueSchema),
  }),
]);

export const noDuplicateId = {
  rule: (data: (ValueDefinition | RemoteValueDefinition)[]) => {
    const set = new Set<string>();
    const ids = new Array<string>();

    for (const def of data) {
      // Skip $fetch definitions for ID uniqueness check, we don't know their IDs yet
      if ('$fetch' in def) continue;

      // $remote specify an ID we can check
      const id = '$remote' in def ? def.$remote : def.id;

      if (set.has(id)) {
        return false;
      }

      set.add(id) && ids.push(id);
    }

    return ids.length === set.size;
  },
  message: {message: 'All value IDs must be unique'},
};

export const ValueDomainSchema: z.ZodType<(ValueDefinition | RemoteValueDefinition)[]> = z
  .array(z.union([ValueDefinitionSchema, RemoteValueDefinitionSchema]))
  .refine(noDuplicateId.rule, noDuplicateId.message);
